<template>
  <div>
    <div
      v-if="isImpersonated"
      class="fixed pin-t pin-x text-xs overflow-hidden bg-neutral-600 text-neutral-50 z-60"
    >
      <div class="flex">
        <div class="flex-1 py-2 px-4">
          You are currently impersonating <b>{{ shared.user.name }}</b>
        </div>
        <button
          type="button"
          class="px-4 font-bold transition-all transition-normal bg-neutral-300 text-neutral-900 hover:bg-neutral-600 hover:text-white outline-none focus:outline-none"
          @click="leaveImpersonation"
        >
          Return To Admin
        </button>
      </div>
    </div>
    <header
      class="bg-white"
      :class="{
        'sticky pin-t z-50 shadow-lg': headerType === 'landing'
      }"
    >
      <div
        class="px-4 sm:px-6 md:px-8 flex justify-between items-center py-2 md:py-4"
      >
        <a
          :href="route('home')"
          class="flex no-underline cursor-pointer text-grey-700 hover:text-grey-900 leading-none items-center"
        >
          <img
            src="/images/apc-logo.png"
            alt="APC UK Logo"
            class="h-8 mr-6"
          />
          <img
            src="/images/zenzic-logo.png"
            alt="Zenzic Logo"
            class="h-5 mr-6"
          />
          <h1 class="text-lg font-light leading-none hidden md:block border-l-2 border-grey-200 pl-4 py-2">
            {{ $config.name }}
          </h1>
        </a>
        <div class="flex items-center justify-end">
          <app-navigation :items="items" />
          <app-actions :state="shared" />
        </div>
      </div>
    </header>
    <main class="relative z-0 pb-16">
      <header
        v-if="$slots.header"
        :class="headerTypeClass"
      >
        <template v-if="hasSidebar">
          <div class="flex">
            <div class="container">
              <slot name="header" />
            </div>
            <div class="w-1/4 min-w-sm" />
          </div>
        </template>
        <template v-else>
          <div class="container">
            <slot name="header" />
          </div>
        </template>
        <nav v-if="$slots.navigation">
          <slot name="navigation" />
        </nav>
      </header>
      <slot />
    </main>
    <footer class="border-t-2 border-grey-200 px-4 sm:px-6 md:px-8">
      <div
        class="container flex flex-wrap text-xs text-center py-6 text-grey-700"
      >
        <nav class="w-full md:w-1/2 md:text-left">
          <ul class="list-reset flex">
            <li class="mr-4">
              <a :href="route('terms')">Terms & Conditions</a>
            </li>
            <li class="mr-4">
              <a :href="route('privacy-policy')">Privacy Policy</a>
            </li>
            <li class="mr-4">
              <a
                href="https://www.apcuk.co.uk?utm_source=mobilise.apc&utm_medium=footer&utm_campaign=mobilise-programme"
                target="_blank"
                rel="noopener"
              >
                APC UK Website
              </a>
            </li>
            <li class="mr-4">
              <a
                href="https://zenzic.io?utm_source=mobilise.apc&utm_medium=footer&utm_campaign=mobilise-programme"
                target="_blank"
                rel="noopener"
              >
                Zenzic Website
              </a>
            </li>
            <li class="mr-4">
              <a :href="route('help.index')"> Contact </a>
            </li>
          </ul>
        </nav>
        <div class="w-full md:w-1/2 md:text-right">
          &copy; {{ new Date().getFullYear() }} The Advanced Propulsion Centre
          UK Ltd. All rights reserved.
        </div>
      </div>
    </footer>
    <fade-transition>
      <div
        v-if="showSharedNotice"
        class="container max-w-md"
        style="position: sticky; bottom: 6rem; z-index: 100;"
      >
        <div
          v-if="shared.session.success"
          class="bg-positive-100 py-4 px-6 text-positive-900 border border-positive-500 rounded flex items-top"
        >
          <icon
            icon="check"
            class="w-8 h-8 mr-6 icons-positive flex-no-shrink"
          />
          <p>
            {{ shared.session.success }}
          </p>
        </div>
        <div
          v-if="shared.session.error"
          class="bg-negative-100 py-4 px-6 text-negative-900 border border-negative-500 rounded flex items-top"
        >
          <icon
            icon="close-circle"
            class="w-8 h-8 mr-6 icons-negative flex-no-shrink"
          />
          <p>
            {{ shared.session.error }}
          </p>
        </div>
      </div>
    </fade-transition>
    <app-cookie />
    <portal-target
      name="modals"
      slim
    />
  </div>
</template>

<script>
import {FadeTransition} from "vue2-transitions";
export default {
  name: "AppLayout",
  components: {
    FadeTransition
  },
  props: {
    hasSidebar: {
      type: Boolean,
      default: false
    },
    headerType: {
      type: String,
      required: false,
      default() {
        return "default";
      }
    }
  },
  data() {
    return {
      showSharedNotice: false,
      items: []
    };
  },
  computed: {
    shared() {
      return this.$parent.state;
    },
    isImpersonated() {
      return this.shared.user.is_impersonated;
    },
    headerTypeClass() {
      return {
        landing:
          "relative z-10 gradient-primary py-6 md:py-8 lg:py-12 px-4 sm:px-6 md:px-8 text-white",
        default:
          (this.hasSidebar ? "" : "px-4 sm:px-6 md:px-8 ") +
          "sticky pin-t z-50 bg-white py-4 border-t border-grey-200 shadow"
      }[this.headerType];
    }
  },
  created() {
    this.buildNavigation();
    if (this.shared.session.success || this.shared.session.error) {
      setTimeout(() => {
        this.showSharedNotice = true;
      }, 200);
      setTimeout(() => {
        this.showSharedNotice = false;
      }, 6000);
    }
  },
  methods: {
    buildNavigation() {
      const user_access = this.shared.user.access_level;
      // Home
      this.items.push({ name: "Dashboard", route: "home" });

      // Monitoring
      if (this.shared.user.is_monitor) {
        this.items.push({ name: "Monitoring", route: "monitoring.index" });
      }

      // Admin Only
      if (user_access === "admin") {
        // Users
        this.items.push({ name: "Users", route: "users.index" });
        // Companies
        this.items.push({ name: "Companies", route: "companies.index" });
      }

      // Help
      if (
        this.shared.user.assessor_competitions_count &&
        this.shared.user.assessor_competitions_count > 0
      ) {
        this.items.push({ name: "Assessments", route: "assessments.index" });
      }

      if (user_access === "user") {
        this.items.push({ name: "Help", route: "help.index" });
      }
    },
    leaveImpersonation() {
      window.location = route("leave-impersonation");
    }
  }
};
</script>
